import { Stack } from '@mui/material';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useFormContext } from '@/components/react-hook-form';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { getDeathSectionDistributionStatus } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/primaryClientDeathSection.utils';
import { PrimaryClientDeathSectionTemplatesMenu } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/PrimaryClientDeathSectionTemplatesMenu';
import {
  DeathSectionDistributionStatus,
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { getDeathEventBasePath } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import { useTemplateId } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/hooks/formHooks';
import { DispositiveProvisionTemplateKind } from '@/types/schema';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { PrimaryClientDeathSectionContextMenu } from '../../contextMenu/PrimaryClientDeathSectionContextMenu';

interface PrimaryClientDeathSectionActionsProps {
  deathEvent: PrimaryClientDeathEvent;
  onAddRecipient?: (
    prefilledValues?: Partial<DispositiveProvisionsFormRecipient>
  ) => void;
  onBulkAddRecipients?: (
    recipients: DispositiveProvisionsFormRecipient[]
  ) => void;
  templateKind?: DispositiveProvisionTemplateKind;
}

export function PrimaryClientDeathSectionActions({
  deathEvent,
  onAddRecipient,
  onBulkAddRecipients,
  templateKind,
}: PrimaryClientDeathSectionActionsProps) {
  const { control } = useFormContext<DispositiveProvisionsFormV2Shape>();
  const canEditHouseholdData = useCanEditDispositiveProvisions();
  const { clientProfileOrEntityOrTestamentaryEntityId } =
    useDispositiveProvisionsContext();

  const [firstDeathRecipients, secondDeathRecipients] = useWatch({
    control,
    name: [
      `${getDeathEventBasePath(PrimaryClientDeathEvent.FIRST_DEATH)}.recipients`,
      `${getDeathEventBasePath(PrimaryClientDeathEvent.SECOND_DEATH)}.recipients`,
    ],
  });

  const status = useMemo(() => {
    return getDeathSectionDistributionStatus(
      deathEvent,
      firstDeathRecipients,
      secondDeathRecipients
    );
  }, [deathEvent, firstDeathRecipients, secondDeathRecipients]);

  // Determine if a template is applied to this death event
  const templateId = useTemplateId({ deathEvent });

  // Check if we're viewing an organization
  const isViewingOrganization = useMemo(() => {
    // If templateKind is present, always return false
    if (templateKind) {
      return false;
    }

    return (
      getPulidKind(clientProfileOrEntityOrTestamentaryEntityId) ===
      PulidKind.ClientOrganization
    );
  }, [clientProfileOrEntityOrTestamentaryEntityId, templateKind]);

  const shouldHideActions =
    templateId ||
    status === DeathSectionDistributionStatus.NO_ASSETS_REMAIN ||
    isViewingOrganization ||
    !canEditHouseholdData;

  // Do not show the actions if a template is applied, if no assets remain, if viewing an organization, or if user can't edit
  if (shouldHideActions) return null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ flexShrink: 0 }}
    >
      {!!onAddRecipient && (
        <Button
          size="xs"
          variant="secondary"
          startIcon={PlusIcon}
          onClick={() => onAddRecipient()}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Add recipient
        </Button>
      )}
      {!templateKind && (
        <>
          <PrimaryClientDeathSectionTemplatesMenu deathEvent={deathEvent} />
          <PrimaryClientDeathSectionContextMenu
            deathEvent={deathEvent}
            onBulkAddRecipients={onBulkAddRecipients}
          />
        </>
      )}
    </Stack>
  );
}
